import * as React from 'react'
import { NewTableColumn, NewTableColumnId, NewTableDefaultDataItem, NewTableLoadMetadata, NewTableLoading } from '@common/components/new-table/new-table.types';
import { Column, Header } from '@tanstack/react-table';
import { useDrop, useDrag } from 'react-dnd';

export type CellProps = {
  getId: {
    root: string;
  };
  loading: NewTableLoading;
  headerCell: Header<NewTableDefaultDataItem, unknown>;
  currentColumnResizeOffset?: number;
  columns: Array<NewTableColumn<NewTableDefaultDataItem>>;
  loadTableMetadata?: NewTableLoadMetadata;
  onSort: (columnId: NewTableColumnId) => void;
  onColumnResizeEnd?: () => void;
  onDrop?: (draggedColumnId: NewTableColumnId, targetColumnId: NewTableColumnId) => void;
  onContextMenu?: (e: React.MouseEvent<HTMLTableCellElement>, columnId: NewTableColumnId) => void;
};

export const useController = (props: CellProps) => {
  const { headerCell, columns, onDrop, onColumnResizeEnd, onContextMenu, onSort } = props;

  const originalColumn = columns.find((x) => x.id === headerCell.id);
  const withDragAndDrop = Boolean(originalColumn?.enableDragAndDrop);

  const TYPES = {
    column: 'column'
  };

  const useDropHof = (): ReturnType<typeof useDrop> => {
    if (withDragAndDrop) {
      return useDrop({
        accept: TYPES.column,
        drop: (draggedColumn: Column<NewTableDefaultDataItem>) => {      
          if (onDrop) {
            onDrop(draggedColumn.id, headerCell.column.id);
          }
        },
      });
    }

    return [undefined, () => null];
  };

  const [, dropRef] = useDropHof();

  const useDragHof = () => {
    if (withDragAndDrop) {
      return useDrag({
        collect: (monitor) => {{
          return {
            isDragging: monitor.isDragging(),
          };
        }},
        item: () => {
          return headerCell.column;
        },
        type: TYPES.column,
      });
    }

    return [{ isDragging: false }, () => null, () => null] as const;
  };

  const [{ isDragging }, dragRef, previewRef] = useDragHof();

  const initMouseUp = React.useCallback(() => {
    document.addEventListener('mouseup', function handle() {
      if (onColumnResizeEnd) {
        onColumnResizeEnd();
      }

      document.removeEventListener('mouseup', handle);
    });
  }, [onColumnResizeEnd]);

  const handleContextMenu = React.useCallback((e: React.MouseEvent<HTMLTableCellElement>) => {
    if (onContextMenu) {
      onContextMenu(e, headerCell.column.id);
    }
  }, [onContextMenu]);

  const handleSort = React.useCallback(() => {
    onSort(headerCell.column.id);
  }, [onSort]);

  return {
    originalColumn,
    withDragAndDrop,
    handleContextMenu: onContextMenu && !originalColumn?.disableActions ? handleContextMenu : undefined,
    handleSort,
    initMouseUp,
    dragRef: withDragAndDrop ? dragRef : undefined,
    dropRef: withDragAndDrop ? dropRef : undefined,
    previewRef: withDragAndDrop ? previewRef : undefined,
    isDragging: withDragAndDrop ? isDragging : undefined,
  };
};